/**
 * Context strip — formats the active map state for the on-screen strip.
 *
 * (See docs/CONVERSATIONAL-AND-COMPOUND-ROADMAP.md, Phase 2.)
 *
 * The strip shows the student (and teacher) what the globe is currently
 * holding: the region in focus, the active BASE choropleth, and any stacked
 * OVERLAYS. It makes layering legible, keeps the conversational context
 * transparent (so auto-clear is never a silent surprise), and is the recovery
 * net when voice mishears — you can see the state drift and correct it.
 *
 * These functions are PURE and unit-tested. They take a plain state object and
 * return display text / chips. The DOM rendering and the live state are wired
 * separately (Phase 2-C), so this file is safe and framework-free.
 *
 * State shape (all fields optional):
 *   {
 *     region:   string | null,                 // e.g. "Africa", "Kenya"
 *     base:     string | { label } | null,     // active base choropleth
 *     overlays: Array<string | { label }>      // stacked overlays, in order
 *   }
 */

import { layerRole } from '../ai/layer-roles.js'

function labelOf(x) {
  if (x == null) return ''
  if (typeof x === 'string') return x.trim()
  if (typeof x === 'object' && x.label != null) return String(x.label).trim()
  return ''
}

function humanize(s) {
  return s ? String(s).replace(/_/g, ' ').trim() : ''
}

/**
 * Derive a short overlay label from the user's own words, e.g.
 * "show the hospitals" → "hospitals", "draw the deviational ellipse" →
 * "deviational ellipse". Strips leading conjunctions, command verbs, and
 * articles. Exported for testing.
 */
export function overlayLabelFromText(text) {
  if (!text) return ''
  let t = String(text).toLowerCase().trim()
  t = t.replace(/^(?:and\s+|also\s+|then\s+|now\s+|just\s+)+/, '')
  t = t.replace(
    /^(?:show|find|draw|add|overlay|put|mark|display|map|highlight|trace|outline|colou?r)\s+/,
    ''
  )
  t = t.replace(/^me\s+/, '')
  t = t.replace(/^(?:the\s+|all\s+(?:the\s+)?|a\s+|some\s+|my\s+)/, '')
  t = t.split(/[,.;]| on (?:the )?map\b/)[0].trim()
  if (t.length > 28) t = t.slice(0, 28).trim()
  return t
}

/**
 * Best-effort short label for the strip from a parsed intent. For a base, the
 * caller passes the rendered choropleth's display name (most reliable). For an
 * overlay we prefer an explicit category, then the user's own words (`text`),
 * then the tool name. Exported for testing.
 */
export function stripLabelForIntent(intent, baseLabel, text) {
  if (!intent) return ''
  if (layerRole(intent) === 'base') {
    return baseLabel || humanize(intent.operation?.attribute) || 'map'
  }
  const explicit =
    humanize(intent.target?.category) ||
    humanize(intent.operation?.filter?.value) ||
    humanize(intent.poi_category)
  if (explicit) return explicit
  const fromText = overlayLabelFromText(text)
  if (fromText) return fromText
  return humanize((intent.type || intent._tool || '').replace(/^geo_/, '')) || 'overlay'
}

/**
 * Pure state transition for the strip: given the previous state and the intent
 * just rendered, return the next state. A BASE render becomes the new base and
 * clears overlays (a fresh base); an OVERLAY render is appended (deduped).
 * `region` and `baseLabel` are supplied by the caller from live app state.
 * Exported for testing.
 */
export function nextContextState(prev, intent, opts) {
  const o = opts || {}
  const p = prev || {}
  const state = {
    region: o.region !== undefined ? o.region : (p.region ?? null),
    base: p.base ?? null,
    overlays: Array.isArray(p.overlays) ? p.overlays.slice() : [],
  }
  if (!intent) return state
  const label = stripLabelForIntent(intent, o.baseLabel, o.text)
  if (layerRole(intent) === 'base') {
    state.base = label || null
    state.overlays = []
  } else if (label && !state.overlays.includes(label)) {
    state.overlays.push(label)
  }
  return state
}

/** Build the ordered list of chips for the strip. Exported for testing. */
export function contextStripChips(state) {
  const s = state || {}
  const chips = []
  const region = labelOf(s.region)
  if (region) chips.push({ kind: 'region', label: region })
  const base = labelOf(s.base)
  if (base) chips.push({ kind: 'base', label: base })
  const overlays = Array.isArray(s.overlays) ? s.overlays : []
  for (const o of overlays) {
    const l = labelOf(o)
    if (l) chips.push({ kind: 'overlay', label: l })
  }
  return chips
}

/**
 * One-line label for the strip, e.g. "Africa · GDP per capita · +ellipse".
 * Overlays are prefixed with "+". Returns '' when there is nothing to show
 * (caller hides the strip). Exported for testing.
 */
export function formatContextStrip(state) {
  const chips = contextStripChips(state)
  if (chips.length === 0) return ''
  return chips.map((c) => (c.kind === 'overlay' ? `+${c.label}` : c.label)).join(' · ')
}

/** True when there's nothing to display. */
export function isContextEmpty(state) {
  return contextStripChips(state).length === 0
}

const contextStrip = {
  contextStripChips,
  formatContextStrip,
  isContextEmpty,
  stripLabelForIntent,
  overlayLabelFromText,
  nextContextState,
}
export default contextStrip
